import { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useLanguage } from '../lib/language';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { motion } from 'framer-motion';
import { Loader2, ArrowLeft, Camera, Dog, Users, User, Globe } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SportTeamPage() {
  const { teamId } = useParams();
  const { language, setLanguage } = useLanguage();
  const [team, setTeam] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchTeam = async () => {
      try {
        const res = await axios.get(`${API}/sport/public/teams/${teamId}`);
        setTeam(res.data);
      } catch (err) {
        console.error('Failed to load team:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchTeam();
  }, [teamId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (!team) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">
          {language === 'pl' ? 'Druzyna nie znaleziona' : 'Team not found'}
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border bg-background/80 backdrop-blur-md sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => window.history.back()} className="text-muted-foreground hover:text-foreground">
              <ArrowLeft className="w-5 h-5" />
            </button>
            <Users className="w-6 h-6" />
            <h1 className="text-xl font-bold">{team.name}</h1>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setLanguage(language === 'pl' ? 'en' : 'pl')}
          >
            <Globe className="w-4 h-4" />
          </Button>
        </div>
      </header>

      {/* Team info */}
      <section className="px-6 py-8 border-b border-border">
        <div className="max-w-6xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <h2 className="text-3xl font-bold">{team.name}</h2>
            {team.club && <p className="text-lg text-muted-foreground mt-1">{team.club}</p>}
            <div className="flex gap-4 mt-3 text-sm text-muted-foreground">
              <span className="flex items-center gap-1">
                <Dog className="w-4 h-4" /> {(team.dogs || []).length} {language === 'pl' ? 'psow' : 'dogs'}
              </span>
              <span className="flex items-center gap-1">
                <User className="w-4 h-4" /> {(team.competitors || []).length} {language === 'pl' ? 'zawodnikow' : 'competitors'}
              </span>
              <span className="flex items-center gap-1">
                <Camera className="w-4 h-4" /> {team.total_photos} {language === 'pl' ? 'zdjec' : 'photos'}
              </span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Dogs */}
      <section className="px-6 py-8 border-b border-border">
        <div className="max-w-6xl mx-auto">
          <h3 className="text-xl font-semibold mb-4">
            {language === 'pl' ? 'Psy' : 'Dogs'} ({(team.dogs || []).length})
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {(team.dogs || []).map((dog, idx) => (
              <motion.div
                key={dog.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: idx * 0.05 }}
              >
                <Link to={`/sport/dog/${dog.id}`}>
                  <Card className="hover:border-primary/30 transition-all cursor-pointer group">
                    <CardContent className="p-4 text-center">
                      <Dog className="w-10 h-10 mx-auto mb-2 text-muted-foreground group-hover:text-primary transition-colors" />
                      <h4 className="font-medium">{dog.name || `#${dog.id.slice(0, 6)}`}</h4>
                      {dog.breed && <p className="text-xs text-muted-foreground">{dog.breed}</p>}
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Competitors */}
      {team.competitors && team.competitors.length > 0 && (
        <section className="px-6 py-8">
          <div className="max-w-6xl mx-auto">
            <h3 className="text-xl font-semibold mb-4">
              {language === 'pl' ? 'Zawodnicy' : 'Competitors'} ({team.competitors.length})
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {team.competitors.map((comp, idx) => (
                <motion.div
                  key={comp.id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.03 }}
                >
                  <Card>
                    <CardContent className="p-4 flex items-center gap-3">
                      <User className="w-8 h-8 text-muted-foreground" />
                      <div>
                        <p className="font-medium">{comp.first_name} {comp.last_name}</p>
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
